import React, { useEffect, useMemo, useState } from 'react';
import ListLayout from 'components/ListLayout';
import { Grid, GridItem } from 'components/ui-kits/Grid';
import DisplayText from 'components/ui-kits/DisplayText';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getMerchantSubscriptionPlans,
  getMerchantSubscriptionProducts,
  getMerchantV2,
  getSubscriptionFees,
  registerMerchantSubscriptionPlan,
  registerMerchantSubscriptionPlanWithId,
  registerSubscriptionPlan,
  updateMerchantDescription
} from 'services/merchantService';
import type {
  MerchantSubscriptionPlan,
  MerchantType,
  SubscriptionFees
} from 'types/Merchant';
import Loader from 'components/Loader';
import { CardWrapper } from 'styles/card';
import styled from 'styled-components';
import { COLORS } from 'styles/variables';
import {
  calculateTax,
  formatDollar,
  formatPercent,
  getPreferPlan,
  handleError,
  handleSuccess,
  isSuccessStatus
} from 'utils';
import { RootState } from 'app/store';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/ui-kits/Button';
import { useHistory } from 'react-router-dom';
import {
  FILTER_SUCCESS_SUBSCRIPTION_LIST,
  MERCHANT_PLAN_CALLBACK_PATHNAME,
  PAYMENT_TOKEN_KEY,
  SELECTED_PLAN_KEY
} from 'const/Const';
import { format } from 'date-fns';
import RetrySubscribePlanModal from 'components/RetrySubscribePlanModal/RetrySubscribePlanModal';
import ActionButton from 'screens/Payment/FinancialReport/ActionButton';
import useGetMerchantDocumentByScreen from 'hooks/useGetMerchantDocumentByScreen';
import { CheckboxSwitch } from 'components/ui-kits/Form/Checkbox';
import { updateSubscriptionPlan } from 'features/subscriptionPlan/subscriptionPlanSlice';
import AcceptTermsStandardPlanModal from 'components/AcceptTermsModal/AcceptTermsStandardPlanModal';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';

const Title = styled(DisplayText)`
  display: flex;
  align-items: baseline;

  .plan__price {
    font-size: 2rem;
    flex: 1;
    text-align: end;
    color: ${COLORS.GREY};
    font-weight: BOLD;
  }
`;

const Plans = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [isChangingSite, setIsChangingSite] = useState(false);
  const tempSelectedPlan = useSelector(
    (state: RootState) => state.subscriptionPlan
  );
  const [isYearly, setIsYearly] = useState(() => {
    return localStorage.getItem('isYearly') === 'true';
  });

  const { merchantSlug } = useSelector((state: RootState) => state.user);
  const { data: dataMerchant } = useQuery(
    'getMerchant',
    ({ signal }) => getMerchantV2(merchantSlug, signal),
    { staleTime: Infinity, enabled: !!merchantSlug }
  );

  const {
    data: dataSubscriptionProducts,
    isLoading: isSubscriptionFeaturesLoading
  } = useQuery(
    'subscriptionFeatures',
    ({ signal }) => getMerchantSubscriptionProducts(signal),
    {
      staleTime: Infinity
    }
  );

  const { data: dataSubscriptionFees, isLoading: isSubscriptionFeesLoading } =
    useQuery(
      'subscriptionFees',
      ({ signal }) =>
        getSubscriptionFees(
          {
            page: 1,
            itemsPerPage: 10
          },
          signal
        ),
      {
        staleTime: Infinity
      }
    );

  const {
    isLoading: isRetryingRegister,
    mutateAsync: mutateMerchantRegisterSubscriptionPlan
  } = useMutation({
    mutationFn: registerMerchantSubscriptionPlan,
    onError: () => handleError(),
    onSettled: () => setIsChangingSite(false)
  });

  const { mutateAsync: mutateUpdateInfo, isLoading: isLoadingUpdateInfo } =
    useMutation({
      mutationFn: (data: { isFinishSetup?: boolean }) =>
        updateMerchantDescription(dataMerchant?.slug ?? '', data)
    });

  const {
    isLoading,
    mutateAsync: mutateRegisterSubscriptionPlanWithId,
    isError
  } = useMutation({
    mutationKey: ['registerSubscription'],
    mutationFn: registerMerchantSubscriptionPlanWithId,
    onSettled: () => {
      queryClient.invalidateQueries(['getMerchant']);
      queryClient.invalidateQueries(['getMerchantSubscriptionPlans']);
      history.replace('/merchant-registration/plans');
      localStorage.removeItem(PAYMENT_TOKEN_KEY);
    },
    onSuccess: async () => {
      const rawPlan = localStorage.getItem(SELECTED_PLAN_KEY);
      const plan = rawPlan ? JSON.parse(rawPlan) : null;

      if (!plan) {
        return;
      }

      await mutateUpdateInfo(
        {
          isFinishSetup: true
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['getMerchant']);
            handleSuccess(
              'Vous avez souscrit avec succès à la formule STANDARD.'
            );
          },
          onError: () => {
            handleError();
          }
        }
      );
      await mutateRegisterSubscriptionPlan(
        {
          slug: merchantSlug,
          data: {
            subscription: `${plan.id}`,
            subscriptionMode: isYearly ? 'yearly' : 'monthly'
          }
        },
        {
          onSettled: () => {
            localStorage.removeItem(SELECTED_PLAN_KEY);
          }
        }
      );
    }
  });

  const {
    mutateAsync: mutateRegisterSubscriptionPlan,
    isLoading: isRegisterSubscriptionPlanLoading
  } = useMutation(
    ({ slug, data }: { slug: string; data: MerchantSubscriptionPlan }) =>
      registerSubscriptionPlan(slug, data),
    {
      onSuccess() {
        queryClient.invalidateQueries({
          queryKey: ['getMerchant']
        });
      }
    }
  );

  const { data: dataSubscriptionPlans, isLoading: isSubscriptionPlansLoading } =
    useQuery('getMerchantSubscriptionPlans', ({ signal }) =>
      getMerchantSubscriptionPlans(FILTER_SUCCESS_SUBSCRIPTION_LIST, signal)
    );

  const { data: dataDocument, isLoading: isGettingMerchantDocuments } =
    useGetMerchantDocumentByScreen('be_online');
  const {
    data: dataDocumentOnlinePayment,
    isLoading: isGettingMerchantOnlineDocuments
  } = useGetMerchantDocumentByScreen(
    'online_payment_and_visible_on_merchant_list'
  );

  const onlinePaymentDocuments =
    dataDocumentOnlinePayment?.['hydra:member'] || [];
  const beOnlineDocuments = dataDocument?.['hydra:member'] || [];
  const rawSubscriptionPlans = dataSubscriptionPlans?.['hydra:member'] || [];
  const subscriptionPlans = rawSubscriptionPlans.filter((item) =>
    isSuccessStatus(item.status)
  );

  const isOnlinePaymentSubmitted = onlinePaymentDocuments.some(
    ({ state }) => state !== 'new'
  );

  const { currentPlan } = useSubscriptionPlan(subscriptionPlans);

  const filteredSubscriptions: SubscriptionFees[] = useMemo(() => {
    const listSubscriptionFeatures =
      dataSubscriptionFees?.['hydra:member'] || [];

    if (!listSubscriptionFeatures.length) return listSubscriptionFeatures;

    const hasBoulangeries = !!dataMerchant?.categories?.some(
      (c: { name?: string }) => /boulangeries/i.test(c?.name ?? '')
    );

    const wanted = hasBoulangeries
      ? ['free boulangeries', 'standard boulangeries']
      : ['free', 'standard'];

    return listSubscriptionFeatures.filter((plan) =>
      wanted.includes(plan.type.trim().toLowerCase())
    );
  }, [dataSubscriptionFees, dataMerchant?.categories]);

  const orderedSubscriptions = useMemo(() => {
    return filteredSubscriptions.sort((a, b) => {
      const score = (x: typeof a) =>
        isFreePlan(x.type) ? 0 : isStandardPlan(x.type) ? 1 : 2;
      return score(a) - score(b);
    });
  }, [filteredSubscriptions]);

  const planPrice = useMemo(() => {
    const price = dataSubscriptionProducts?.find((item) =>
      isYearly ? item.method === 'yearly' : item.method === 'monthly'
    );

    return price?.amountTTC || 0;
  }, [dataSubscriptionProducts, isYearly]);

  useEffect(() => {
    dispatch(updateSubscriptionPlan(dataMerchant?.subscription || {}));
  }, [dispatch, dataMerchant?.subscription]);

  useEffect(() => {
    localStorage.setItem('planPrice', planPrice.toString());
  }, [planPrice]);

  const preferredPlan = !!currentPlan
    ? dataMerchant?.subscription
    : getPreferPlan(tempSelectedPlan, dataMerchant?.subscription);

  // handle back button bfcache that causes loader to presist the state
  // https://web.dev/articles/bfcache#spa
  useEffect(() => {
    const hideLoader = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsChangingSite(false);
        dispatch(updateSubscriptionPlan(preferredPlan!));
      }
    };

    window.addEventListener('pageshow', hideLoader);

    return () => {
      window.removeEventListener('pageshow', hideLoader);
    };
  }, [dispatch, preferredPlan]);

  useEffect(() => {
    const paymentToken = localStorage.getItem(PAYMENT_TOKEN_KEY);
    const isCallbackFromPayment =
      location.pathname === MERCHANT_PLAN_CALLBACK_PATHNAME;
    const url = new URL(window.location.href);
    const isFromEmail = url.searchParams.get('email') === 'true';

    if (isCallbackFromPayment && (paymentToken || isFromEmail)) {
      mutateRegisterSubscriptionPlanWithId();
    }
  }, [mutateRegisterSubscriptionPlanWithId]);

  useEffect(() => {
    const isYearly = dataMerchant?.setting.subscriptionMode === 'yearly';
    const isYearlyStorage = localStorage.getItem('isYearly') === 'true';
    const preferredValue =
      isYearlyStorage !== isYearly ? isYearlyStorage : isYearly;

    setIsYearly(preferredValue);

    localStorage.setItem('isYearly', `${preferredValue}`);
  }, [dataMerchant?.setting.subscriptionMode]);

  const handlePlanSelected = (item: SubscriptionFees) => {
    if (isOnlinePaymentSubmitted && isStandardPlan(item.type)) {
      setShowDialog(true);
    } else {
      const selectedPlan = orderedSubscriptions.find((s) => s.id === item.id);

      if (!selectedPlan) {
        return;
      }

      dispatch(updateSubscriptionPlan(selectedPlan));

      const isBeOnlineInProgress =
        !dataMerchant?.enabled &&
        beOnlineDocuments.every((doc) => doc.state === 'new');

      if (!isStandardPlan(selectedPlan.type) && !!dataMerchant?.isFinishSetup) {
        return;
      }

      history.push({
        pathname: isBeOnlineInProgress
          ? '/merchant-registration/be-online'
          : '/merchant-registration/online-payment',
        state: { allowGuardBypass: true }
      });
    }
  };

  const mutateSubscribeStandard = async () => {
    const standardPlan = orderedSubscriptions.find((s) =>
      isStandardPlan(s.type)
    );

    if (!standardPlan) {
      return;
    }

    localStorage.setItem(
      SELECTED_PLAN_KEY,
      JSON.stringify({ type: standardPlan.type, id: standardPlan.id })
    );

    const {
      data: { redirect_url, payment_token }
    } = await mutateMerchantRegisterSubscriptionPlan(window.origin);

    if (redirect_url && payment_token) {
      localStorage.setItem(PAYMENT_TOKEN_KEY, payment_token);

      setIsChangingSite(true);
      window.location.href = redirect_url;
    }
  };

  const isLoadingAll =
    isSubscriptionFeaturesLoading ||
    isSubscriptionFeesLoading ||
    isSubscriptionPlansLoading ||
    isRetryingRegister ||
    isRegisterSubscriptionPlanLoading ||
    isLoading ||
    isChangingSite ||
    isGettingMerchantDocuments ||
    isGettingMerchantOnlineDocuments ||
    isLoadingUpdateInfo;

  const onAcceptStandardSubmitted = async () => {
    await mutateSubscribeStandard();
  };

  const { beforeTax } = calculateTax(formatDollar(planPrice * 100));

  return (
    <div style={{ height: '100%', minHeight: '100vh' }}>
      {isLoadingAll && <Loader />}
      <RetrySubscribePlanModal
        retryFn={async () => {
          await mutateSubscribeStandard();
        }}
        defaultShow={isError}
        key={`error-dialog-${isError}`}
      />
      {showDialog && (
        <AcceptTermsStandardPlanModal
          screenName={'online_payment_and_visible_on_merchant_list'}
          isClickNoThanks={false}
          onClose={() => setShowDialog(false)}
          onCustomAcceptNoThanks={onAcceptStandardSubmitted}
          hasSubscriptionPlan={!!currentPlan}
        />
      )}
      <ListLayout>
        <DisplayText
          margin="2rem 0 1.5rem 0"
          label="Formules"
          fontWeight="LIGHT"
          fontSize="XXL"
          color="BLACK_2"
        />
        <DisplayText label="Choisissez votre formule pour activer votre boutique en ligne ou modifiez votre formule." />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            width: '100%',
            padding: '1rem 0 2rem'
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '1rem',
              paddingRight: '4rem'
            }}>
            <DisplayText
              label="Par mois"
              fontSize="L"
              fontWeight="MEDIUM"
              className="label-option"
            />
            <CheckboxSwitch
              id="duration"
              name="duration"
              switchSize="small"
              checked={isYearly}
              handleChange={(value) => {
                const isYearly = value.target.checked;
                setIsYearly(isYearly);
                localStorage.setItem('isYearly', `${isYearly}`);
              }}>
              <DisplayText
                label="Par an"
                fontSize="L"
                fontWeight="MEDIUM"
                className="label-option"
              />
            </CheckboxSwitch>
          </div>
          <Grid
            col={2}
            start="gutter"
            end="large"
            marginRight={true}
            {...{ rowGap: '3rem', width: '100%' }}>
            {orderedSubscriptions.map((item) => {
              const isStandardRenderItem = isStandardPlan(item.type);
              const titleName = isStandardRenderItem ? 'STANDARD' : 'FREE';
              const colStart = isStandardRenderItem ? 4 : 1;
              const colEnd = isStandardRenderItem ? 6 : 3;
              const selectedPlan = preferredPlan?.id === item.id;
              const isDisabled = selectedPlan || !!currentPlan;

              const latestEndDate = currentPlan
                ? format(new Date(currentPlan.endDate), 'dd/MM/yyyy')
                : '';

              const isCurrentPlanCancelled = !!currentPlan?.isCancelled;
              const isShowNextPayment =
                isStandardRenderItem &&
                isDisabled &&
                !!dataMerchant?.isFinishSetup &&
                !!latestEndDate;

              return (
                <GridItem key={item.id} colStart={colStart} colEnd={colEnd}>
                  <CardWrapper>
                    <div>
                      <Title fontSize="XXL" color="RED" className="plan__title">
                        <span className="plan__name">{titleName}</span>
                        <span className="plan__price">
                          {isStandardRenderItem ? beforeTax : formatDollar(0)}€
                          HT / {isYearly ? 'an' : 'mois'}
                        </span>
                      </Title>
                      {!isStandardRenderItem && (
                        <FreePlanContent
                          fixAmount={item.fixAmount}
                          percentage={item.percentage}
                          merchantType={item.type}
                          minimumAmount={item.minimumAmount}
                        />
                      )}
                      {isStandardRenderItem && (
                        <>
                          {isYearly ? (
                            <DisplayText
                              fontSize="M"
                              textAlign="end"
                              margin="1rem 0 1rem 0"
                              color="GREY">
                              60€ d&apos;économie sans engagement
                            </DisplayText>
                          ) : (
                            <DisplayText
                              fontSize="M"
                              textAlign="end"
                              margin="1rem 0 1rem 0"
                              color="GREY">
                              Sans engagement
                            </DisplayText>
                          )}
                          <StandardPlanContent
                            fixAmount={item.fixAmount}
                            percentage={item.percentage}
                            merchantType={item.type}
                            minimumAmount={item.minimumAmount}
                          />
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: '2rem'
                        }}>
                        <Button
                          key={item.id}
                          variant={isDisabled ? 'cancel' : 'primary'}
                          isFluid={false}
                          isUpperCase={false}
                          fontWeight="BOLD"
                          disabled={isDisabled}
                          onClick={() => {
                            handlePlanSelected(item);
                          }}>
                          {selectedPlan
                            ? 'Votre formule'
                            : 'Sélectionner cette formule'}
                        </Button>
                        {isStandardRenderItem &&
                          isDisabled &&
                          !!dataMerchant?.isFinishSetup &&
                          !!dataMerchant.subscriptionPlans.length && (
                            <ActionButton
                              planType="financial_report"
                              currentPlan={currentPlan}
                              showUnsubButton
                            />
                          )}
                      </div>
                      {isCurrentPlanCancelled && isStandardRenderItem ? (
                        <DisplayText fontSize="M" fontWeight="BOLD">
                          Fin de l&apos;abonnement : {latestEndDate}
                        </DisplayText>
                      ) : (
                        isShowNextPayment &&
                        isStandardRenderItem && (
                          <DisplayText fontSize="M" fontWeight="BOLD">
                            Prochain paiement : {latestEndDate}
                          </DisplayText>
                        )
                      )}
                    </div>
                  </CardWrapper>
                </GridItem>
              );
            })}
          </Grid>
        </div>
      </ListLayout>
    </div>
  );
};

export default Plans;

const isStandardPlan = (t = '') =>
  /^(standard)(\s+boulangeries)?$/i.test(t.trim());
const isFreePlan = (t = '') => /^(free)(\s+boulangeries)?$/i.test(t.trim());

const FreePlanContent = ({
  fixAmount,
  percentage,
  merchantType,
  minimumAmount
}: {
  fixAmount: number;
  percentage: number;
  merchantType: MerchantType;
  minimumAmount: number;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '10rem',
        paddingTop: '4rem'
      }}>
      <DisplayText fontSize="M" fontWeight="BOLD">
        <span style={{ textDecoration: 'underline' }}>
          Ce qui est compris :
        </span>
        <ul
          style={{
            margin: '1rem 2rem',
            fontWeight: 'normal',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
          <li>Votre site web de vente en ligne</li>
          <li>Click and Collect, Drive, Livraison</li>
          <li>Créez des réductions (%)</li>
          <li>Consultez vos rapports de ventes quotidiens</li>
          <li>Support Chat (7j/7 de 9h à 21h)</li>
          <li>Paiement sur place</li>
          <br />
        </ul>
      </DisplayText>

      <div>
        <DisplayText fontSize="M" fontWeight="BOLD">
          <span style={{ textDecoration: 'underline' }}>
            Services optionnels
          </span>{' '}
          (non inclus, activables à tout moment) :
        </DisplayText>

        <ul
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            margin: '1rem 0 0rem 2rem',
            listStyleType: 'disc',
            fontSize: '1.6rem'
          }}>
          <li>
            Mise en place <span style={{ fontWeight: 'bold' }}>249€ HT</span> :
            <DisplayText
              fontWeight="BOLD"
              fontSize="M"
              margin="1rem 0"
              color="BLACK">
              offert en activant le paiement en ligne
            </DisplayText>
            <ul
              style={{
                margin: '1rem 0 0 4rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                fontSize: 'M',
                listStyleType: 'circle'
              }}>
              <li>Paramétrage de votre boutique</li>
              <li>Création de vos produits et options</li>
              <li>Vérification finale</li>
            </ul>
          </li>
          <li>
            Paiement en ligne
            <ul
              style={{
                margin: '1rem 0 0 4rem',
                fontWeight: 'bold',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                fontSize: 'M',
                listStyleType: 'circle'
              }}>
              {merchantType === 'Free' ? (
                <li>
                  {formatPercent((percentage * 100).toString(), 1)} % +{' '}
                  {formatDollar(fixAmount * 100)}€ HT / transaction
                </li>
              ) : (
                <>
                  <li>
                    {formatPercent((percentage * 100).toString(), 1)} % HT /
                    transaction ≤ {minimumAmount}€
                  </li>
                  <li>
                    {formatPercent((percentage * 100).toString(), 1)} % +{' '}
                    {formatDollar(fixAmount * 100)}€ HT / transaction &gt;{' '}
                    {minimumAmount}€
                  </li>
                </>
              )}
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
};

const StandardPlanContent = ({
  fixAmount,
  percentage,
  merchantType,
  minimumAmount
}: {
  fixAmount: number;
  percentage: number;
  merchantType: MerchantType;
  minimumAmount: number;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '4rem'
      }}>
      <DisplayText fontSize="M" fontWeight="BOLD">
        <span style={{ textDecoration: 'underline' }}>
          Ce qui est compris :
        </span>
        <DisplayText margin="1rem 0" fontSize="M" fontWeight="BOLD">
          Formule FREE
        </DisplayText>
        <DisplayText margin="1rem 0" fontSize="M" fontWeight="BOLD">
          +
        </DisplayText>
        <ul
          style={{
            margin: '1rem 2rem',
            fontWeight: 'normal',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
          <li>Référencement & Indexation sur Google</li>
          <li>Intégration Facebook, Google, Instagram</li>
          <li>Intégration sur votre site web existant</li>
          <li>Mise à jour du menu 1 fois par an</li>
          <li>Support téléphonique (7j/7 de 9h à 21h)</li>
          <li>Accompagnement marketing</li>
          <li>Téléchargez vos rapports de ventes</li>
        </ul>
      </DisplayText>

      <div>
        <DisplayText fontSize="M" fontWeight="BOLD">
          <span style={{ textDecoration: 'underline' }}>
            Services optionnels
          </span>{' '}
          (non inclus, activables à tout moment) :
        </DisplayText>
        <ul
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            margin: '1rem 0 0 2rem',
            listStyleType: 'disc',
            fontSize: '1.6rem'
          }}>
          <li>
            Mise en place <span style={{ fontWeight: 'bold' }}>199€ HT</span> :
            <DisplayText
              fontWeight="BOLD"
              fontSize="M"
              margin="1rem 0"
              color="BLACK">
              offert en activant le paiement en ligne
            </DisplayText>
            <ul
              style={{
                margin: '1rem 0 0 4rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                fontSize: 'M',
                listStyleType: 'circle'
              }}>
              <li>Paramétrage de votre boutique</li>
              <li>Création de vos produits et options</li>
              <li>Vérification finale</li>
            </ul>
          </li>
          <li>
            Paiement en ligne
            <ul
              style={{
                margin: '1rem 0 0 4rem',
                fontWeight: 'bold',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                fontSize: 'M',
                listStyleType: 'circle'
              }}>
              {merchantType === 'Standard' ? (
                <li>
                  {formatPercent((percentage * 100).toString(), 1)} % +{' '}
                  {formatDollar(fixAmount * 100)}€ HT / transaction
                </li>
              ) : (
                <>
                  <li>
                    {formatPercent((percentage * 100).toString(), 1)} % HT /
                    transaction ≤ {minimumAmount}€
                  </li>
                  <li>
                    {formatPercent((percentage * 100).toString(), 1)} % +{' '}
                    {formatDollar(fixAmount * 100)}€ HT / transaction &gt;{' '}
                    {minimumAmount}€
                  </li>
                </>
              )}
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
};
